/**
 * YouTube and Vimeo links. The API stores one canonical form per provider
 * (https://www.youtube.com/watch?v=ID, https://vimeo.com/ID); these helpers
 * accept the same range of links as it does, so forms can check before saving,
 * and turn a stored link into something to embed.
 */

export interface VideoInfo {
  provider: 'youtube' | 'vimeo'
  id: string
  /** The canonical link, as the API stores it. */
  url: string
  /** Player URL, set to start playing as it loads (it's only loaded on click). */
  embedUrl: string
  /** A still to show before playing; YouTube only. */
  thumbnail: string | null
}

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/
const VIMEO_ID = /^[0-9]{1,12}$/

export function parseVideo(raw: string | null | undefined): VideoInfo | null {
  const input = raw?.trim()
  if (!input || input.length > 500) return null

  let url: URL
  try {
    url = new URL(input.includes('://') ? input : `https://${input}`)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null

  const host = url.hostname
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/^m\./, '')
  const parts = url.pathname.split('/').filter(Boolean)

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = parts.at(-1)
    if (!id || !VIMEO_ID.test(id)) return null
    return {
      provider: 'vimeo',
      id,
      url: `https://vimeo.com/${id}`,
      embedUrl: `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`,
      thumbnail: null,
    }
  }

  let id: string | null = null
  if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'music.youtube.com') {
    if (url.pathname === '/watch') id = url.searchParams.get('v')
    else if (parts.length === 2 && ['embed', 'shorts', 'live', 'v'].includes(parts[0]))
      id = parts[1]
  } else if (host === 'youtu.be' && parts.length === 1) {
    id = parts[0]
  }
  if (!id || !YOUTUBE_ID.test(id)) return null

  return {
    provider: 'youtube',
    id,
    url: `https://www.youtube.com/watch?v=${id}`,
    // youtube-nocookie: no tracking cookies until the viewer plays it.
    embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`,
    thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
  }
}

export const VIDEO_LINK_ERROR =
  'Use a YouTube or Vimeo link, like https://www.youtube.com/watch?v=…'

/** Whether a video field's text can be saved (blank or a usable link). */
export const videoFieldValid = (value: string) => value.trim() === '' || parseVideo(value) !== null

/** Field text -> what to save: the canonical link, or null when blank. */
export const videoFieldValue = (value: string) => parseVideo(value)?.url ?? null
